/**
 * Formatting values the way a console does.
 *
 * The sandbox prints what the learner's code logs, and predict questions ask
 * them to type that output exactly. So this formatter is not cosmetic — it
 * defines what counts as a correct answer. If it renders `[1, 2]` and the
 * question's expected output says `[ 1, 2 ]`, the learner is wrong about
 * nothing and marked wrong anyway.
 *
 * Node's `util.inspect` and the browser devtools console disagree on details,
 * and neither is available in a Worker. This is a deliberate, documented middle
 * — close to Node's, because that is the output most JavaScript learners see
 * quoted in tutorials.
 *
 * ── Constraint that shapes this file ────────────────────────────────────────
 *
 * These functions are serialized with `Function.prototype.toString()` and
 * injected into the sandbox Worker as source text (see runJs.ts). That means
 * they must be COMPLETELY SELF-CONTAINED: no imports, no module-scope
 * constants, no closure over anything. A helper referenced from outside would
 * serialize as a name that does not exist inside the Worker, and the failure
 * would look like a mysterious ReferenceError in the learner's own code.
 *
 * The upside of paying that price: one implementation, unit-tested in Node,
 * genuinely running in the Worker. Not a copy that drifts.
 */

/** Format a single value, as it would appear NESTED inside a structure. */
export function inspect(value: unknown, depth = 0, seen: unknown[] = []): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'

  const type = typeof value

  if (type === 'string') {
    // Single quotes, like Node — not JSON.stringify's double quotes. This is
    // the form learners see in every tutorial, and predict answers are typed
    // from memory of that form.
    const s = value as string
    return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t')}'`
  }
  if (type === 'number') return Object.is(value, -0) ? '-0' : String(value)
  if (type === 'boolean') return String(value)
  if (type === 'bigint') return `${String(value)}n`
  if (type === 'symbol') return String(value)
  if (type === 'function') {
    const fn = value as { name?: string }
    return fn.name ? `[Function: ${fn.name}]` : '[Function (anonymous)]'
  }

  // Cycles. Without this, `const a = {}; a.self = a; console.log(a)` recurses
  // until the stack dies — and the learner sees a stack overflow they did not
  // write, in code that a real console prints happily.
  if (seen.indexOf(value) !== -1) return '[Circular *1]'
  if (depth > 4) return Array.isArray(value) ? '[Array]' : '[Object]'

  const nextSeen = seen.concat([value])

  if (value instanceof Error) {
    return `${value.name}: ${value.message}`
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString()
  }
  if (value instanceof RegExp) return String(value)

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]'
    const parts: string[] = []
    for (let i = 0; i < value.length; i++) {
      // Holes in a sparse array are not the same as undefined, and the
      // difference is a real JavaScript lesson.
      parts.push(i in value ? inspect(value[i], depth + 1, nextSeen) : '<1 empty item>')
    }
    return `[ ${parts.join(', ')} ]`
  }

  if (value instanceof Map) {
    if (value.size === 0) return 'Map(0) {}'
    const parts: string[] = []
    value.forEach((v, k) => {
      parts.push(`${inspect(k, depth + 1, nextSeen)} => ${inspect(v, depth + 1, nextSeen)}`)
    })
    return `Map(${value.size}) { ${parts.join(', ')} }`
  }

  if (value instanceof Set) {
    if (value.size === 0) return 'Set(0) {}'
    const parts: string[] = []
    value.forEach((v) => parts.push(inspect(v, depth + 1, nextSeen)))
    return `Set(${value.size}) { ${parts.join(', ')} }`
  }

  const obj = value as Record<string, unknown>
  const keys = Object.keys(obj)
  if (keys.length === 0) return '{}'

  const parts: string[] = []
  for (const key of keys) {
    // Keys print bare when they are valid identifiers, quoted when not —
    // matching what a console does, and what people expect to retype.
    const safe = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)
      ? key
      : `'${key.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
    parts.push(`${safe}: ${inspect(obj[key], depth + 1, nextSeen)}`)
  }
  return `{ ${parts.join(', ')} }`
}

/**
 * Format one `console.log(...)` call.
 *
 * The one rule that surprises people: a TOP-LEVEL string prints bare, while a
 * string nested inside an object or array prints quoted. `console.log('hi')`
 * gives `hi`; `console.log(['hi'])` gives `[ 'hi' ]`. That is genuine console
 * behaviour, not a quirk of this implementation, and predict questions rely
 * on it being right.
 */
export function formatLogArgs(args: unknown[], fmt: (value: unknown) => string): string {
  const parts: string[] = []
  for (const arg of args) {
    parts.push(typeof arg === 'string' ? arg : fmt(arg))
  }
  return parts.join(' ')
}

/**
 * `fmt` is a required parameter with no default, and that is not an oversight.
 *
 * The obvious signature is `formatLogArgs(args)` calling `inspect` directly.
 * It works in `npm run dev` and breaks in production, which is the worst
 * possible combination. These functions are injected into the Worker as source
 * text, and esbuild mangles module-scope names during the production build —
 * so the serialized body would call `n(...)` while the bootstrap defines
 * `var inspect`, and the sandbox would fail only after deploy.
 *
 * Taking the formatter as an argument removes the cross-reference entirely.
 * Each function serializes to something that depends on nothing but its own
 * parameters.
 */
